import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Typography,
  Tabs,
  Table,
  Button,
  Space,
  Tag,
  Statistic,
  Row,
  Col,
  Modal,
  Form,
  Input,
  DatePicker,
  InputNumber,
  Select,
  Switch,
  Descriptions,
  message,
  Popconfirm,
  Spin,
  Empty,
  Alert,
} from 'antd';
import {
  ArrowLeftOutlined,
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  TrophyOutlined,
  TeamOutlined,
  CalendarOutlined,
  BarChartOutlined,
  GiftOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { adminApi } from '@/services/api';
import dayjs from 'dayjs';
import type { LiveScheduleCalendarLink } from '@/types/matchPushes';
import { AddMatchesFromCalendarModal } from './AddMatchesFromCalendarModal';

const { Title, Text } = Typography;

interface LiveEvent {
  id: string;
  name: string;
  description?: string;
  type: string;
  status: string;
  tileStartTime: string;
  tileEndTime: string;
  eventStartTime: string;
  eventEndTime: string;
  platform: string;
  targetCountries: string;
  participantCount?: number;
  matchCount?: number;
  createdAt: string;
}

interface MatchSchedule extends Partial<LiveScheduleCalendarLink> {
  id: string;
  name: string;
  matchNumber: number;
  team1Code: string;
  team1Name: string;
  team2Code: string;
  team2Name: string;
  startTime: string;
  endTime: string;
  status: string;
  realWinner?: string;
  realWinnerAnnounced: boolean;
  team1TotalWins: number;
  team2TotalWins: number;
  isSemiFinal: boolean;
  isFinal: boolean;
  group?: string;
}

interface RafflePeriod {
  id: string;
  title: string;
  startTime: string;
  endTime: string;
  pointsPerTicket: number;
  winnerSelected: boolean;
  winnerId?: string;
  winnerName?: string;
  isActive: boolean;
}

interface EventStats {
  participantCount: number;
  matchCount: number;
  totalMatchesPlayed: number;
  totalPointsAwarded: number;
  correctPredictions: number;
  registeredUsers: number;
}

interface LeaderboardEntry {
  rank: number;
  userId: string;
  username?: string;
  totalPoints: number;
  correctPredictions: number;
  matchesPlayed: number;
}

interface Participant {
  id: string;
  userId: string;
  platform: string;
  totalPoints: number;
  correctPredictions: number;
  matchesPlayed: number;
  matchesWon: number;
  isRegistered: boolean;
  registeredName?: string;
  registeredEmail?: string;
  joinedAt: string;
}

const statusColors: Record<string, string> = {
  draft: 'default',
  scheduled: 'blue',
  tile_visible: 'cyan',
  active: 'green',
  completed: 'default',
  cancelled: 'red',
};

const matchStatusColors: Record<string, string> = {
  upcoming: 'blue',
  live: 'green',
  completed: 'default',
  cancelled: 'red',
};

const typeLabels: Record<string, string> = {
  world_cup: 'World Cup',
  ipl: 'IPL',
  t20_league: 'T20 League',
  test_series: 'Test Series',
  special_event: 'Special Event',
};

export const LiveEventShow: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [event, setEvent] = useState<LiveEvent | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  // Match Schedules state
  const [schedules, setSchedules] = useState<MatchSchedule[]>([]);
  const [schedulesLoading, setSchedulesLoading] = useState(false);
  const [scheduleModalVisible, setScheduleModalVisible] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<MatchSchedule | null>(null);
  const [scheduleForm] = Form.useForm();

  // Winner modal state
  const [winnerModalVisible, setWinnerModalVisible] = useState(false);
  const [selectedScheduleForWinner, setSelectedScheduleForWinner] = useState<MatchSchedule | null>(
    null
  );
  const [winnerTeamCode, setWinnerTeamCode] = useState<string>('');

  // Add matches from the cricket calendar
  const [calendarModalOpen, setCalendarModalOpen] = useState(false);

  // Raffle state
  const [rafflePeriods, setRafflePeriods] = useState<RafflePeriod[]>([]);
  const [raffleLoading, setRaffleLoading] = useState(false);

  // Stats state
  const [stats, setStats] = useState<EventStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);

  // Leaderboard state
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [leaderboardLoading, setLeaderboardLoading] = useState(false);
  const [leaderboardTotal, setLeaderboardTotal] = useState(0);

  // Participants state
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [participantsLoading, setParticipantsLoading] = useState(false);
  const [participantsTotal, setParticipantsTotal] = useState(0);

  // Fetch event details
  const fetchEvent = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const response = await adminApi.getLiveEvent(id);
      setEvent(response.data);
    } catch (error) {
      message.error('Failed to load event');
      navigate('/live-events');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  // Fetch match schedules
  const fetchSchedules = useCallback(async () => {
    if (!id) return;
    setSchedulesLoading(true);
    try {
      const response = await adminApi.getMatchSchedules(id);
      const data = response.data?.data || response.data;
      setSchedules(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to fetch schedules:', error);
      setSchedules([]);
    } finally {
      setSchedulesLoading(false);
    }
  }, [id]);

  // Fetch raffle periods
  const fetchRafflePeriods = useCallback(async () => {
    if (!id) return;
    setRaffleLoading(true);
    try {
      const response = await adminApi.getRafflePeriods(id);
      const data = response.data?.data || response.data;
      setRafflePeriods(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to fetch raffle periods:', error);
      setRafflePeriods([]);
    } finally {
      setRaffleLoading(false);
    }
  }, [id]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    if (!id) return;
    setStatsLoading(true);
    try {
      const response = await adminApi.getLiveEventStats(id);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
      setStats(null);
    } finally {
      setStatsLoading(false);
    }
  }, [id]);

  // Fetch leaderboard
  const fetchLeaderboard = useCallback(
    async (page = 1, limit = 50) => {
      if (!id) return;
      setLeaderboardLoading(true);
      try {
        const response = await adminApi.getLiveEventLeaderboard(id, { page, limit });
        const data = response.data;
        setLeaderboard(data.entries || []);
        setLeaderboardTotal(data.total || 0);
      } catch (error) {
        console.error('Failed to fetch leaderboard:', error);
        setLeaderboard([]);
      } finally {
        setLeaderboardLoading(false);
      }
    },
    [id]
  );

  // Fetch participants
  const fetchParticipants = useCallback(
    async (page = 1, limit = 50) => {
      if (!id) return;
      setParticipantsLoading(true);
      try {
        const response = await adminApi.getLiveEventParticipants(id, { page, limit });
        const data = response.data;
        setParticipants(data.data || []);
        setParticipantsTotal(data.total || 0);
      } catch (error) {
        console.error('Failed to fetch participants:', error);
        setParticipants([]);
      } finally {
        setParticipantsLoading(false);
      }
    },
    [id]
  );

  useEffect(() => {
    fetchEvent();
  }, [fetchEvent]);

  // Fetch data based on active tab
  useEffect(() => {
    if (activeTab === 'schedules') {
      fetchSchedules();
    } else if (activeTab === 'raffle') {
      fetchRafflePeriods();
    } else if (activeTab === 'stats') {
      fetchStats();
    } else if (activeTab === 'leaderboard') {
      fetchLeaderboard();
    } else if (activeTab === 'participants') {
      fetchParticipants();
    }
  }, [
    activeTab,
    fetchSchedules,
    fetchRafflePeriods,
    fetchStats,
    fetchLeaderboard,
    fetchParticipants,
  ]);

  // Schedule CRUD handlers
  const handleCreateSchedule = () => {
    setEditingSchedule(null);
    scheduleForm.resetFields();
    setScheduleModalVisible(true);
  };

  const handleEditSchedule = (schedule: MatchSchedule) => {
    setEditingSchedule(schedule);
    scheduleForm.setFieldsValue({
      ...schedule,
      startTime: dayjs(schedule.startTime),
      endTime: dayjs(schedule.endTime),
    });
    setScheduleModalVisible(true);
  };

  const handleScheduleSubmit = async (values: Record<string, unknown>) => {
    if (!id) return;
    try {
      const payload = {
        ...values,
        startTime: (values.startTime as dayjs.Dayjs).toISOString(),
        endTime: (values.endTime as dayjs.Dayjs).toISOString(),
      };

      if (editingSchedule) {
        await adminApi.updateMatchSchedule(id, editingSchedule.id, payload);
        message.success('Schedule updated');
      } else {
        await adminApi.createMatchSchedule(id, payload);
        message.success('Schedule created');
      }
      setScheduleModalVisible(false);
      fetchSchedules();
    } catch (error) {
      message.error('Failed to save schedule');
    }
  };

  const handleDeleteSchedule = async (scheduleId: string) => {
    if (!id) return;
    try {
      await adminApi.deleteMatchSchedule(id, scheduleId);
      message.success('Schedule deleted');
      fetchSchedules();
    } catch (error) {
      message.error('Failed to delete schedule');
    }
  };

  // Set winner handlers
  const handleOpenWinnerModal = (schedule: MatchSchedule) => {
    setSelectedScheduleForWinner(schedule);
    setWinnerTeamCode('');
    setWinnerModalVisible(true);
  };

  const handleSetWinner = async () => {
    if (!id || !selectedScheduleForWinner || !winnerTeamCode) return;
    try {
      await adminApi.setRealWinner(id, selectedScheduleForWinner.id, winnerTeamCode);
      message.success('Winner set successfully');
      setWinnerModalVisible(false);
      fetchSchedules();
    } catch (error) {
      message.error('Failed to set winner');
    }
  };

  // Raffle winner selection
  const handleSelectRaffleWinner = async (periodId: string) => {
    if (!id) return;
    try {
      const response = await adminApi.selectRaffleWinner(id, periodId);
      if (response.data.success) {
        message.success(`Winner selected: ${response.data.winnerName || response.data.winnerId}`);
        fetchRafflePeriods();
      } else {
        message.warning(response.data.message || 'No eligible participants');
      }
    } catch (error) {
      message.error('Failed to select winner');
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 100 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!event) {
    return <Alert message="Event not found" type="error" />;
  }

  // Schedule columns
  const scheduleColumns = [
    {
      title: '#',
      dataIndex: 'matchNumber',
      key: 'matchNumber',
      width: 60,
    },
    {
      title: 'Match',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record: MatchSchedule) => (
        <Space direction="vertical" size={0}>
          <Text strong>{name}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.team1Name} vs {record.team2Name}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Schedule',
      key: 'schedule',
      render: (_: unknown, record: MatchSchedule) => (
        <Space direction="vertical" size={0}>
          <Text style={{ fontSize: 12 }}>{new Date(record.startTime).toLocaleString()}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            to {new Date(record.endTime).toLocaleString()}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={matchStatusColors[status]}>{status?.toUpperCase()}</Tag>
      ),
    },
    {
      title: 'Winner',
      key: 'winner',
      render: (_: unknown, record: MatchSchedule) =>
        record.realWinnerAnnounced ? (
          <Tag color="gold" icon={<TrophyOutlined />}>
            {record.realWinner}
          </Tag>
        ) : (
          <Space size={4} wrap>
            <Text type="secondary">-</Text>
            {record.suggestedWinner && (
              <Tag color="cyan">
                Calendar suggests{' '}
                {record.suggestedWinner === 'team1' ? record.team1Name : record.team2Name}
              </Tag>
            )}
          </Space>
        ),
    },
    {
      title: 'Votes',
      key: 'votes',
      render: (_: unknown, record: MatchSchedule) => (
        <Space>
          <Text>
            {record.team1Code}: {record.team1TotalWins}
          </Text>
          <Text type="secondary">|</Text>
          <Text>
            {record.team2Code}: {record.team2TotalWins}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: unknown, record: MatchSchedule) => (
        <Space>
          {!record.realWinnerAnnounced && (
            <Button
              size="small"
              icon={<TrophyOutlined />}
              onClick={() => handleOpenWinnerModal(record)}
            >
              Set Winner
            </Button>
          )}
          <Button size="small" icon={<EditOutlined />} onClick={() => handleEditSchedule(record)} />
          <Popconfirm
            title="Delete this schedule?"
            onConfirm={() => handleDeleteSchedule(record.id)}
          >
            <Button size="small" icon={<DeleteOutlined />} danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Raffle columns
  const raffleColumns = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: 'Period',
      key: 'period',
      render: (_: unknown, record: RafflePeriod) => (
        <Space direction="vertical" size={0}>
          <Text style={{ fontSize: 12 }}>{new Date(record.startTime).toLocaleString()}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            to {new Date(record.endTime).toLocaleString()}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Points/Ticket',
      dataIndex: 'pointsPerTicket',
      key: 'pointsPerTicket',
    },
    {
      title: 'Status',
      key: 'status',
      render: (_: unknown, record: RafflePeriod) => (
        <Space>
          {record.isActive && <Tag color="green">Active</Tag>}
          {record.winnerSelected ? (
            <Tag color="gold" icon={<CheckCircleOutlined />}>
              Winner Selected
            </Tag>
          ) : (
            <Tag icon={<ClockCircleOutlined />}>Pending</Tag>
          )}
        </Space>
      ),
    },
    {
      title: 'Winner',
      key: 'winner',
      render: (_: unknown, record: RafflePeriod) =>
        record.winnerSelected ? (
          <Space direction="vertical" size={0}>
            <Text strong>{record.winnerName || 'Unknown'}</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.winnerId}
            </Text>
          </Space>
        ) : (
          <Text type="secondary">-</Text>
        ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: unknown, record: RafflePeriod) => (
        <Space>
          {!record.winnerSelected && !record.isActive && (
            <Popconfirm
              title="Select a random winner for this period?"
              onConfirm={() => handleSelectRaffleWinner(record.id)}
            >
              <Button type="primary" size="small" icon={<GiftOutlined />}>
                Select Winner
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  // Leaderboard columns
  const leaderboardColumns = [
    {
      title: 'Rank',
      dataIndex: 'rank',
      key: 'rank',
      width: 80,
      render: (rank: number) => (
        <Text strong style={{ color: rank <= 3 ? '#faad14' : undefined }}>
          #{rank}
        </Text>
      ),
    },
    {
      title: 'User',
      key: 'user',
      render: (_: unknown, record: LeaderboardEntry) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.username || 'Unknown'}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.userId}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Points',
      dataIndex: 'totalPoints',
      key: 'totalPoints',
      render: (points: number) => <Text strong>{points.toLocaleString()}</Text>,
    },
    {
      title: 'Correct Predictions',
      dataIndex: 'correctPredictions',
      key: 'correctPredictions',
    },
    {
      title: 'Matches Played',
      dataIndex: 'matchesPlayed',
      key: 'matchesPlayed',
    },
  ];

  // Participants columns
  const participantColumns = [
    {
      title: 'User ID',
      dataIndex: 'userId',
      key: 'userId',
      render: (userId: string) => (
        <Text copyable style={{ fontSize: 12 }}>
          {userId}
        </Text>
      ),
    },
    {
      title: 'Platform',
      dataIndex: 'platform',
      key: 'platform',
      render: (platform: string) => <Tag>{platform?.toUpperCase()}</Tag>,
    },
    {
      title: 'Points',
      dataIndex: 'totalPoints',
      key: 'totalPoints',
      render: (points: number) => points.toLocaleString(),
    },
    {
      title: 'Predictions',
      dataIndex: 'correctPredictions',
      key: 'correctPredictions',
    },
    {
      title: 'Matches',
      key: 'matches',
      render: (_: unknown, record: Participant) => (
        <Text>
          {record.matchesWon}/{record.matchesPlayed} won
        </Text>
      ),
    },
    {
      title: 'Registered',
      key: 'registered',
      render: (_: unknown, record: Participant) =>
        record.isRegistered ? (
          <Space direction="vertical" size={0}>
            <Tag color="green">Yes</Tag>
            <Text type="secondary" style={{ fontSize: 11 }}>
              {record.registeredName}
            </Text>
          </Space>
        ) : (
          <Tag>No</Tag>
        ),
    },
    {
      title: 'Joined',
      dataIndex: 'joinedAt',
      key: 'joinedAt',
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
  ];

  return (
    <div>
      <div className="page-header">
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/live-events')} />
          <div>
            <Title level={3} style={{ margin: 0 }}>
              {event.name}
            </Title>
            <Space>
              <Tag color={statusColors[event.status]}>{event.status?.toUpperCase()}</Tag>
              <Text type="secondary">{typeLabels[event.type] || event.type}</Text>
            </Space>
          </div>
        </Space>
        <Button
          type="primary"
          icon={<EditOutlined />}
          onClick={() => navigate(`/live-events/${id}/edit`)}
        >
          Edit Event
        </Button>
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: 'overview',
            label: 'Overview',
            icon: <BarChartOutlined />,
            children: (
              <Row gutter={24}>
                <Col xs={24} lg={16}>
                  <Card title="Event Information">
                    <Descriptions bordered column={2}>
                      <Descriptions.Item label="Name" span={2}>
                        {event.name}
                      </Descriptions.Item>
                      <Descriptions.Item label="Description" span={2}>
                        {event.description || '-'}
                      </Descriptions.Item>
                      <Descriptions.Item label="Type">{typeLabels[event.type]}</Descriptions.Item>
                      <Descriptions.Item label="Status">
                        <Tag color={statusColors[event.status]}>{event.status?.toUpperCase()}</Tag>
                      </Descriptions.Item>
                      <Descriptions.Item label="Platform">
                        {event.platform?.toUpperCase()}
                      </Descriptions.Item>
                      <Descriptions.Item label="Countries">
                        {event.targetCountries}
                      </Descriptions.Item>
                      <Descriptions.Item label="Tile Start">
                        {new Date(event.tileStartTime).toLocaleString()}
                      </Descriptions.Item>
                      <Descriptions.Item label="Tile End">
                        {new Date(event.tileEndTime).toLocaleString()}
                      </Descriptions.Item>
                      <Descriptions.Item label="Event Start">
                        {new Date(event.eventStartTime).toLocaleString()}
                      </Descriptions.Item>
                      <Descriptions.Item label="Event End">
                        {new Date(event.eventEndTime).toLocaleString()}
                      </Descriptions.Item>
                    </Descriptions>
                  </Card>
                </Col>
                <Col xs={24} lg={8}>
                  <Card title="Quick Stats">
                    <Row gutter={[16, 16]}>
                      <Col span={12}>
                        <Statistic
                          title="Participants"
                          value={event.participantCount || 0}
                          prefix={<TeamOutlined />}
                        />
                      </Col>
                      <Col span={12}>
                        <Statistic
                          title="Matches"
                          value={event.matchCount || 0}
                          prefix={<CalendarOutlined />}
                        />
                      </Col>
                    </Row>
                  </Card>
                </Col>
              </Row>
            ),
          },
          {
            key: 'schedules',
            label: 'Match Schedules',
            icon: <CalendarOutlined />,
            children: (
              <Card
                title="Match Schedules"
                extra={
                  <Space>
                    <Button
                      icon={<ReloadOutlined />}
                      onClick={fetchSchedules}
                      loading={schedulesLoading}
                    >
                      Refresh
                    </Button>
                    <Button icon={<CalendarOutlined />} onClick={() => setCalendarModalOpen(true)}>
                      Add matches from calendar
                    </Button>
                    <Button type="primary" icon={<PlusOutlined />} onClick={handleCreateSchedule}>
                      Add Match
                    </Button>
                  </Space>
                }
              >
                <Table
                  dataSource={schedules}
                  columns={scheduleColumns}
                  rowKey="id"
                  loading={schedulesLoading}
                  scroll={{ x: 1000 }}
                  pagination={false}
                  locale={{ emptyText: <Empty description="No matches scheduled" /> }}
                />
              </Card>
            ),
          },
          {
            key: 'raffle',
            label: 'Raffle',
            icon: <GiftOutlined />,
            children: (
              <Card
                title="Raffle Periods"
                extra={
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={fetchRafflePeriods}
                    loading={raffleLoading}
                  >
                    Refresh
                  </Button>
                }
              >
                <Table
                  dataSource={rafflePeriods}
                  columns={raffleColumns}
                  rowKey="id"
                  loading={raffleLoading}
                  pagination={false}
                  locale={{ emptyText: <Empty description="No raffle periods configured" /> }}
                />
              </Card>
            ),
          },
          {
            key: 'stats',
            label: 'Statistics',
            icon: <BarChartOutlined />,
            children: (
              <Card
                title="Event Statistics"
                loading={statsLoading}
                extra={
                  <Button icon={<ReloadOutlined />} onClick={fetchStats} loading={statsLoading}>
                    Refresh
                  </Button>
                }
              >
                {stats ? (
                  <Row gutter={[24, 24]}>
                    <Col xs={12} md={8} lg={4}>
                      <Statistic
                        title="Participants"
                        value={stats.participantCount}
                        prefix={<TeamOutlined />}
                      />
                    </Col>
                    <Col xs={12} md={8} lg={4}>
                      <Statistic
                        title="Match Schedules"
                        value={stats.matchCount}
                        prefix={<CalendarOutlined />}
                      />
                    </Col>
                    <Col xs={12} md={8} lg={4}>
                      <Statistic title="Matches Played" value={stats.totalMatchesPlayed} />
                    </Col>
                    <Col xs={12} md={8} lg={4}>
                      <Statistic title="Points Awarded" value={stats.totalPointsAwarded} />
                    </Col>
                    <Col xs={12} md={8} lg={4}>
                      <Statistic title="Correct Predictions" value={stats.correctPredictions} />
                    </Col>
                    <Col xs={12} md={8} lg={4}>
                      <Statistic title="Registered Users" value={stats.registeredUsers} />
                    </Col>
                  </Row>
                ) : (
                  <Empty description="No statistics available" />
                )}
              </Card>
            ),
          },
          {
            key: 'leaderboard',
            label: 'Leaderboard',
            icon: <TrophyOutlined />,
            children: (
              <Card
                title="Event Leaderboard"
                extra={
                  <Space>
                    <Text type="secondary">{leaderboardTotal} total entries</Text>
                    <Button
                      icon={<ReloadOutlined />}
                      onClick={() => fetchLeaderboard()}
                      loading={leaderboardLoading}
                    >
                      Refresh
                    </Button>
                  </Space>
                }
              >
                <Table
                  dataSource={leaderboard}
                  columns={leaderboardColumns}
                  rowKey="userId"
                  loading={leaderboardLoading}
                  pagination={{
                    total: leaderboardTotal,
                    pageSize: 50,
                    onChange: (page) => fetchLeaderboard(page),
                  }}
                  locale={{ emptyText: <Empty description="No leaderboard entries" /> }}
                />
              </Card>
            ),
          },
          {
            key: 'participants',
            label: 'Participants',
            icon: <TeamOutlined />,
            children: (
              <Card
                title="Event Participants"
                extra={
                  <Space>
                    <Text type="secondary">{participantsTotal} total participants</Text>
                    <Button
                      icon={<ReloadOutlined />}
                      onClick={() => fetchParticipants()}
                      loading={participantsLoading}
                    >
                      Refresh
                    </Button>
                  </Space>
                }
              >
                <Table
                  dataSource={participants}
                  columns={participantColumns}
                  rowKey="id"
                  loading={participantsLoading}
                  scroll={{ x: 900 }}
                  pagination={{
                    total: participantsTotal,
                    pageSize: 50,
                    onChange: (page) => fetchParticipants(page),
                  }}
                  locale={{ emptyText: <Empty description="No participants yet" /> }}
                />
              </Card>
            ),
          },
        ]}
      />

      {/* Schedule Create/Edit Modal */}
      <Modal
        title={editingSchedule ? 'Edit Match Schedule' : 'Add Match Schedule'}
        open={scheduleModalVisible}
        onCancel={() => setScheduleModalVisible(false)}
        footer={null}
        width={700}
      >
        <Form form={scheduleForm} layout="vertical" onFinish={handleScheduleSubmit}>
          <Form.Item name="name" label="Match Name" rules={[{ required: true }]}>
            <Input placeholder="e.g., Match 1: IND vs AUS" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="matchNumber" label="Match Number" rules={[{ required: true }]}>
                <InputNumber min={1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="group" label="Group">
                <Input placeholder="e.g., A" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="status" label="Status">
                <Select>
                  <Select.Option value="upcoming">Upcoming</Select.Option>
                  <Select.Option value="live">Live</Select.Option>
                  <Select.Option value="completed">Completed</Select.Option>
                  <Select.Option value="cancelled">Cancelled</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="team1Code" label="Team 1 Code" rules={[{ required: true }]}>
                <Input placeholder="e.g., IND" maxLength={10} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="team1Name" label="Team 1 Name" rules={[{ required: true }]}>
                <Input placeholder="e.g., India" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="team2Code" label="Team 2 Code" rules={[{ required: true }]}>
                <Input placeholder="e.g., AUS" maxLength={10} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="team2Name" label="Team 2 Name" rules={[{ required: true }]}>
                <Input placeholder="e.g., Australia" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="startTime" label="Start Time" rules={[{ required: true }]}>
                <DatePicker showTime style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="endTime" label="End Time" rules={[{ required: true }]}>
                <DatePicker showTime style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="isSemiFinal" label="Semi-Final" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="isFinal" label="Final" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item>
            <Space>
              <Button onClick={() => setScheduleModalVisible(false)}>Cancel</Button>
              <Button type="primary" htmlType="submit">
                {editingSchedule ? 'Update' : 'Create'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {id && (
        <AddMatchesFromCalendarModal
          open={calendarModalOpen}
          eventId={id}
          onClose={() => setCalendarModalOpen(false)}
          onAdded={fetchSchedules}
        />
      )}

      {/* Set Winner Modal */}
      <Modal
        title="Set Match Winner"
        open={winnerModalVisible}
        onCancel={() => setWinnerModalVisible(false)}
        onOk={handleSetWinner}
        okText="Confirm Winner"
        okButtonProps={{ disabled: !winnerTeamCode }}
      >
        {selectedScheduleForWinner && (
          <div>
            <Text>
              Select the winner for <strong>{selectedScheduleForWinner.name}</strong>
            </Text>
            {selectedScheduleForWinner.suggestedWinner && (
              <Alert
                type="info"
                showIcon
                style={{ marginTop: 12 }}
                message={
                  <span>
                    <Tag color="cyan">Suggested</Tag>
                    The cricket calendar says{' '}
                    <strong>
                      {selectedScheduleForWinner.suggestedWinner === 'team1'
                        ? selectedScheduleForWinner.team1Name
                        : selectedScheduleForWinner.team2Name}
                    </strong>{' '}
                    won. You still confirm the winner.
                  </span>
                }
              />
            )}
            <div style={{ marginTop: 16 }}>
              <Space size="large">
                <Button
                  type={
                    winnerTeamCode === selectedScheduleForWinner.team1Code ? 'primary' : 'default'
                  }
                  size="large"
                  onClick={() => setWinnerTeamCode(selectedScheduleForWinner.team1Code)}
                >
                  {selectedScheduleForWinner.team1Code} - {selectedScheduleForWinner.team1Name}
                  {selectedScheduleForWinner.suggestedWinner === 'team1' && ' (suggested)'}
                </Button>
                <Button
                  type={
                    winnerTeamCode === selectedScheduleForWinner.team2Code ? 'primary' : 'default'
                  }
                  size="large"
                  onClick={() => setWinnerTeamCode(selectedScheduleForWinner.team2Code)}
                >
                  {selectedScheduleForWinner.team2Code} - {selectedScheduleForWinner.team2Name}
                  {selectedScheduleForWinner.suggestedWinner === 'team2' && ' (suggested)'}
                </Button>
              </Space>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default LiveEventShow;
